import {
  AirHandlerTypeCodeType,
  HyperHeatFilterValueType,
  SystemOptionType,
  SystemTypeFilterValueType
} from 'data/types';
import { abbreviateNumber } from 'utils/helpers';
import { airHandlerTypeCodes } from 'data/constants';

export type SystemListFilterState = {
  configIds: Set<string>;
  airHandlerTypes: Set<AirHandlerTypeCodeType>;
  condenserSeries: Set<string>;
  condenserBtus: Set<number>;
  hyperHeat: Set<HyperHeatFilterValueType>;
  systemTypes: Set<SystemTypeFilterValueType>;
  refrigerants: Set<string>;
};

export type SystemListFilterUniverse = {
  airHandlerTypes: AirHandlerTypeCodeType[];
  condenserSeries: string[];
  condenserBtus: number[];
  hyperHeat: HyperHeatFilterValueType[];
  systemTypes: SystemTypeFilterValueType[];
  // Verbatim refrigerant attribute strings (e.g. 'R-410A'); the value IS
  // the display label.
  refrigerants: string[];
};

export const HYPER_HEAT_LABELS: Record<HyperHeatFilterValueType, string> = {
  hyperHeat: 'Ultra Cooling & Heating',
  standard: 'Standard'
};

export const SYSTEM_TYPE_LABELS: Record<SystemTypeFilterValueType, string> = {
  diy: 'DIY',
  standard: 'Standard'
};

export function formatBtuBucket(btu: number): string {
  return `${abbreviateNumber(btu)} BTU`;
}

export function roundBtuBucket(btu: number): number {
  return Math.round(btu / 1000) * 1000;
}

function mapHyperHeat(value: boolean): HyperHeatFilterValueType {
  return value ? 'hyperHeat' : 'standard';
}

function mapSystemType(diy: boolean): SystemTypeFilterValueType {
  return diy ? 'diy' : 'standard';
}

export function collectAirHandlerTypes(
  options: SystemOptionType[]
): AirHandlerTypeCodeType[] {
  const airHandlerTypes = new Set<AirHandlerTypeCodeType>();
  options.forEach((option) => {
    option.configuration.condensers.forEach((cond) => {
      cond.zones.forEach((zone) => {
        const label = zone.selectedProduct?.type;
        if (!label) return;
        const code = airHandlerTypeCodes[label];
        if (code) airHandlerTypes.add(code);
      });
    });
  });
  return Array.from(airHandlerTypes).sort((a, b) => a.localeCompare(b));
}

// Keyed by trimmed seriesCode; the label IS the seriesCode itself.
export function buildCondenserSeriesLabels(
  options: SystemOptionType[]
): Record<string, string> {
  const labels: Record<string, string> = {};
  options.forEach((option) => {
    option.configuration.condensers.forEach((cond) => {
      const code = cond.selectedProduct?.seriesCode?.trim();
      if (code) labels[code] = code;
    });
  });
  return labels;
}

export function buildFilterUniverse(
  options: SystemOptionType[]
): SystemListFilterUniverse {
  const condenserSeries = new Set<string>();
  const condenserBtus = new Set<number>();
  const hyperHeat = new Set<HyperHeatFilterValueType>();
  const systemTypes = new Set<SystemTypeFilterValueType>();

  options.forEach((option) => {
    option.configuration.condensers.forEach((cond) => {
      const product = cond.selectedProduct;
      if (!product) return;
      // condenserSeries values are seriesCodes, not series names.
      const seriesCode = product.seriesCode?.trim();
      if (seriesCode) condenserSeries.add(seriesCode);
      if (product.coolingBTU > 0) {
        condenserBtus.add(roundBtuBucket(product.coolingBTU));
      }
      hyperHeat.add(mapHyperHeat(product.hyperHeat));
      systemTypes.add(mapSystemType(product.diy));
    });
  });

  return {
    airHandlerTypes: collectAirHandlerTypes(options),
    condenserSeries: Array.from(condenserSeries).sort((a, b) =>
      a.localeCompare(b)
    ),
    condenserBtus: Array.from(condenserBtus).sort((a, b) => a - b),
    hyperHeat: Array.from(hyperHeat).sort(),
    systemTypes: Array.from(systemTypes).sort(),
    // The client-side product shape carries no refrigerant attribute; the
    // dimension only exists when a server universe provides it (same
    // degradation seriesCode had before it reached the client).
    refrigerants: []
  };
}

// Union across per-config universes, re-sorted with the same per-dimension
// semantics buildFilterUniverse uses (localeCompare for strings, ascending
// for BTUs, default sort for hyperHeat).
export function mergeFilterUniverses(
  universes: SystemListFilterUniverse[]
): SystemListFilterUniverse {
  const airHandlerTypes = new Set<AirHandlerTypeCodeType>();
  const condenserSeries = new Set<string>();
  const condenserBtus = new Set<number>();
  const hyperHeat = new Set<HyperHeatFilterValueType>();
  const systemTypes = new Set<SystemTypeFilterValueType>();
  const refrigerants = new Set<string>();

  universes.forEach((universe) => {
    universe.airHandlerTypes.forEach((v) => airHandlerTypes.add(v));
    universe.condenserSeries.forEach((v) => condenserSeries.add(v));
    universe.condenserBtus.forEach((v) => condenserBtus.add(v));
    universe.hyperHeat.forEach((v) => hyperHeat.add(v));
    universe.systemTypes.forEach((v) => systemTypes.add(v));
    universe.refrigerants.forEach((v) => refrigerants.add(v));
  });

  return {
    airHandlerTypes: Array.from(airHandlerTypes).sort((a, b) =>
      a.localeCompare(b)
    ),
    condenserSeries: Array.from(condenserSeries).sort((a, b) =>
      a.localeCompare(b)
    ),
    condenserBtus: Array.from(condenserBtus).sort((a, b) => a - b),
    hyperHeat: Array.from(hyperHeat).sort(),
    systemTypes: Array.from(systemTypes).sort(),
    refrigerants: Array.from(refrigerants).sort((a, b) => a.localeCompare(b))
  };
}

export function defaultFilterState(
  configIds: string[],
  universe: SystemListFilterUniverse
): SystemListFilterState {
  return {
    configIds: new Set(configIds),
    airHandlerTypes: new Set(universe.airHandlerTypes),
    condenserSeries: new Set(universe.condenserSeries),
    condenserBtus: new Set(universe.condenserBtus),
    hyperHeat: new Set(universe.hyperHeat),
    systemTypes: new Set(universe.systemTypes),
    refrigerants: new Set(universe.refrigerants)
  };
}

function reconcileDimension<T>(
  prev: ReadonlySet<T>,
  known: ReadonlyArray<T>,
  seen: ReadonlySet<T>
): Set<T> {
  const knownSet = new Set(known);
  const next = new Set<T>();
  prev.forEach((value) => {
    if (knownSet.has(value)) next.add(value);
  });
  known.forEach((value) => {
    if (!seen.has(value)) next.add(value);
  });
  if (next.size === 0 && knownSet.size > 0) {
    knownSet.forEach((value) => next.add(value));
  }
  return next;
}

function unionSet<T>(
  base: ReadonlySet<T>,
  additions: ReadonlyArray<T>
): Set<T> {
  const result = new Set(base);
  additions.forEach((value) => result.add(value));
  return result;
}

export type FilterReconcileMemory = {
  configIds: Set<string>;
  airHandlerTypes: Set<AirHandlerTypeCodeType>;
  condenserSeries: Set<string>;
  condenserBtus: Set<number>;
  hyperHeat: Set<HyperHeatFilterValueType>;
  systemTypes: Set<SystemTypeFilterValueType>;
  refrigerants: Set<string>;
};

export function createFilterReconcileMemory(): FilterReconcileMemory {
  return {
    configIds: new Set<string>(),
    airHandlerTypes: new Set<AirHandlerTypeCodeType>(),
    condenserSeries: new Set<string>(),
    condenserBtus: new Set<number>(),
    hyperHeat: new Set<HyperHeatFilterValueType>(),
    systemTypes: new Set<SystemTypeFilterValueType>(),
    refrigerants: new Set<string>()
  };
}

function isSameSet<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function isSameFilterState(
  a: SystemListFilterState,
  b: SystemListFilterState
): boolean {
  return (
    isSameSet(a.configIds, b.configIds) &&
    isSameSet(a.airHandlerTypes, b.airHandlerTypes) &&
    isSameSet(a.condenserSeries, b.condenserSeries) &&
    isSameSet(a.condenserBtus, b.condenserBtus) &&
    isSameSet(a.hyperHeat, b.hyperHeat) &&
    isSameSet(a.systemTypes, b.systemTypes) &&
    isSameSet(a.refrigerants, b.refrigerants)
  );
}

export function reconcileFilterState(
  prev: SystemListFilterState,
  configIds: ReadonlyArray<string>,
  universe: SystemListFilterUniverse,
  seen: FilterReconcileMemory
): SystemListFilterState {
  const next: SystemListFilterState = {
    configIds: reconcileDimension(prev.configIds, configIds, seen.configIds),
    airHandlerTypes: reconcileDimension(
      prev.airHandlerTypes,
      universe.airHandlerTypes,
      seen.airHandlerTypes
    ),
    condenserSeries: reconcileDimension(
      prev.condenserSeries,
      universe.condenserSeries,
      seen.condenserSeries
    ),
    condenserBtus: reconcileDimension(
      prev.condenserBtus,
      universe.condenserBtus,
      seen.condenserBtus
    ),
    hyperHeat: reconcileDimension(
      prev.hyperHeat,
      universe.hyperHeat,
      seen.hyperHeat
    ),
    systemTypes: reconcileDimension(
      prev.systemTypes,
      universe.systemTypes,
      seen.systemTypes
    ),
    refrigerants: reconcileDimension(
      prev.refrigerants,
      universe.refrigerants,
      seen.refrigerants
    )
  };

  if (isSameFilterState(prev, next)) return prev;
  return next;
}

export function extendFilterReconcileMemory(
  prev: FilterReconcileMemory,
  configIds: ReadonlyArray<string>,
  universe: SystemListFilterUniverse
): FilterReconcileMemory {
  return {
    configIds: unionSet(prev.configIds, configIds),
    airHandlerTypes: unionSet(prev.airHandlerTypes, universe.airHandlerTypes),
    condenserSeries: unionSet(prev.condenserSeries, universe.condenserSeries),
    condenserBtus: unionSet(prev.condenserBtus, universe.condenserBtus),
    hyperHeat: unionSet(prev.hyperHeat, universe.hyperHeat),
    systemTypes: unionSet(prev.systemTypes, universe.systemTypes),
    refrigerants: unionSet(prev.refrigerants, universe.refrigerants)
  };
}

export function memorizedUniverseFromMemory(
  memory: FilterReconcileMemory
): SystemListFilterUniverse {
  return {
    airHandlerTypes: Array.from(memory.airHandlerTypes).sort((a, b) =>
      a.localeCompare(b)
    ),
    condenserSeries: Array.from(memory.condenserSeries).sort((a, b) =>
      a.localeCompare(b)
    ),
    condenserBtus: Array.from(memory.condenserBtus).sort((a, b) => a - b),
    hyperHeat: Array.from(memory.hyperHeat).sort(),
    systemTypes: Array.from(memory.systemTypes).sort(),
    refrigerants: Array.from(memory.refrigerants).sort((a, b) =>
      a.localeCompare(b)
    )
  };
}

export function systemMatchesFilters(
  option: SystemOptionType,
  state: SystemListFilterState
): boolean {
  return state.configIds.has(option.configuration.uniqueId);
}

export function hasAnyFilterableDimension(
  universe: SystemListFilterUniverse
): boolean {
  return (
    universe.airHandlerTypes.length > 1 ||
    universe.condenserSeries.length > 1 ||
    universe.condenserBtus.length > 1 ||
    universe.hyperHeat.length > 1 ||
    universe.systemTypes.length > 1 ||
    universe.refrigerants.length > 1
  );
}

export function isFilterStateFiltered(
  state: SystemListFilterState,
  universe: SystemListFilterUniverse
): boolean {
  return (
    state.airHandlerTypes.size < universe.airHandlerTypes.length ||
    state.condenserSeries.size < universe.condenserSeries.length ||
    state.condenserBtus.size < universe.condenserBtus.length ||
    state.hyperHeat.size < universe.hyperHeat.length ||
    state.systemTypes.size < universe.systemTypes.length ||
    state.refrigerants.size < universe.refrigerants.length
  );
}
